import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer } from "../../../../ui/components/Drawer";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { Badge } from "../../../../ui/components/Badge";
import { Loader } from "../../../../ui/components/Loader";
import { Alert } from "../../../../ui/components/Alert";
import DocumentViewer from "../../../../components/DocumentViewer";
import {
  FeatherExternalLink,
  FeatherRefreshCw,
  FeatherX,
} from "@subframe/core";
import { getSignedUrlForDocument } from "../../../../services/api";
import { useAgentReviewDetail } from "../hooks/useAgentReview";
import { AgentReviewListItem } from "../../domain/entities/AgentReviewEntities";
import AgentReviewStatusBadge from "./AgentReviewStatusBadge";
import FindingCard from "./FindingCard";

/**
 * ZOUR-67 — everything about one bill's review, beside the document.
 *
 * The PDF is on the left because every finding is a claim about it, and a
 * claim you cannot check against the source is a claim you have to take on
 * trust. `DocumentViewer` is the same component the review page uses, so the
 * document behaves identically in both places.
 *
 * Posting does not happen here. "Open in review" goes to `/bills/review`,
 * which owns editing and Post; this page only ever tells you what to look at.
 */

interface Props {
  bill: AgentReviewListItem | null;
  canEdit: boolean;
  onClose: () => void;
  /** Called after a re-check so the list can pick up the new status. */
  onChanged?: () => void;
  /**
   * Where this drawer was opened from, which decides the footer only. Everything
   * above it — the document, the banners, the findings — is identical, because
   * the same bill must not read differently depending on which page you came
   * from.
   *
   * `queue` (default) is the Agent Review page: Re-check, plus both ways into
   * the review page. `dashboard` is the bills list, where the accountant is
   * mid-task and wants one way out: Close, or open the bill in a new tab so the
   * list they were working through is still behind them.
   */
  variant?: "queue" | "dashboard";
}

export const AgentReviewDrawer: React.FC<Props> = ({
  bill,
  canEdit,
  onClose,
  onChanged,
  variant = "queue",
}) => {
  const navigate = useNavigate();
  const billId = bill?.bill_id ?? null;
  const { detail, loading, error, rechecking, recheck, disposition } =
    useAgentReviewDetail(billId);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPdfUrl(null);
    if (billId === null) return;

    (async () => {
      try {
        const response = await getSignedUrlForDocument({
          documentId: billId,
          documentType: "bill",
          expiration: 3600,
        });
        if (!cancelled && response?.success) {
          setPdfUrl(response.data.signedUrl ?? null);
        }
      } catch {
        // A missing document is not a broken drawer. The findings still stand
        // on their own evidence, and the agent says so in `cannot_verify` when
        // it could not read the PDF either.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [billId]);

  const run = detail?.run ?? null;

  const { blocking, advisory } = useMemo(() => {
    const findings = detail?.findings ?? [];
    return {
      blocking: findings.filter((f) => f.finding_group === "document_mismatch"),
      advisory: findings.filter((f) => f.finding_group === "booking_advisory"),
    };
  }, [detail?.findings]);

  const handleRecheck = async () => {
    await recheck();
    onChanged?.();
  };

  return (
    <Drawer open={bill !== null} onOpenChange={(open) => !open && onClose()}>
      <Drawer.Content className="w-full max-w-[1100px]">
        {/* Header */}
        <div className="flex w-full items-start justify-between gap-4 border-b border-solid border-neutral-border px-6 py-4">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-heading-3 font-heading-3 text-default-font">
                {bill?.bill_number || `Bill ${bill?.bill_id ?? ""}`}
              </span>
              {bill && (
                <AgentReviewStatusBadge status={bill.status} stale={bill.stale} />
              )}
            </div>
            <span className="text-body font-body text-subtext-color">
              {bill?.vendor_name || "Unknown vendor"}
              {bill?.date ? ` · ${bill.date}` : ""}
            </span>
          </div>
          <IconButton icon={<FeatherX />} onClick={onClose} />
        </div>

        <div className="flex w-full flex-1 flex-col overflow-auto lg:flex-row">
          {/* The document */}
          {/* `DocumentViewer` is `self-stretch`, which only gives it height in a
              row-direction parent. This column is vertical, so the viewer gets
              `flex-1` and a floor of its own instead — without it the PDF pane
              renders its toolbar and nothing else. */}
          <div className="flex w-full flex-col border-b border-solid border-neutral-border lg:w-1/2 lg:border-b-0 lg:border-r">
            {pdfUrl ? (
              <DocumentViewer
                documentId={billId ?? undefined}
                documentType="bill"
                pdfUrl={pdfUrl}
                fileName={`${bill?.bill_number || "bill"}.pdf`}
                className="w-full flex-1 min-h-[480px]"
                onError={() => undefined}
              />
            ) : (
              <div className="flex min-h-[480px] w-full flex-1 items-center justify-center p-6">
                <span className="text-body font-body text-subtext-color">
                  {billId === null ? "" : "Loading the document…"}
                </span>
              </div>
            )}
          </div>

          {/* The review */}
          <div className="flex w-full flex-col gap-4 p-6 lg:w-1/2">
            {loading && (
              <div className="flex w-full items-center gap-2">
                <Loader />
                <span className="text-body font-body text-subtext-color">
                  Loading the review…
                </span>
              </div>
            )}

            {error && <Alert variant="warning" title="" description={error} />}

            {!loading && run === null && (
              <Alert
                variant="neutral"
                title="Not reviewed yet"
                description="The background check has not looked at this bill. It will be picked up on the next run."
              />
            )}

            {run?.status === "error" && (
              <Alert
                variant="error"
                title="The last check failed"
                description={run.error || "No reason was recorded."}
              />
            )}

            {/* Above the findings, because it changes how every one of them
                should be read: without the document the agent could only check
                the booking against history, and "nothing contradicts the PDF"
                was never asked. */}
            {run?.status === "done" && bill?.pdf_available === false && (
              <Alert
                variant="warning"
                title="The document could not be read, so this bill cannot be Ready"
                description={
                  bill.pdf_error
                    ? `${bill.pdf_error}. The findings below come from the booking and this vendor's history only.`
                    : "The findings below come from the booking and this vendor's history only; nothing here was checked against the invoice."
                }
              />
            )}

            {/* A posted bill's JSON is the corrected version, so a backtest run
                describes the bill BEFORE the accountant touched it. Without
                this line every finding below reads as an outstanding problem
                with a bill that was in fact fixed and posted months ago. */}
            {bill?.is_posted && run && (
              <Alert
                variant="neutral"
                title="This bill is already posted"
                description={
                  run.trigger_type === "backtest"
                    ? "Reviewed as it arrived, before the accountant's edits. The findings below describe the bill at intake, not as it stands now."
                    : "Reviewed as it stands now, after the accountant's edits."
                }
              />
            )}

            {/* Same rule as the list: a stale run describes a version of the
                bill that no longer exists, so say so before anything below is
                read as current. */}
            {bill?.stale && run && (
              <Alert
                variant="warning"
                title="From an earlier check"
                description="This bill has changed since it was reviewed. Everything below describes the older version; the background check will look at it again."
              />
            )}

            {run?.summary && (
              <span
                className={`text-body font-body text-default-font ${
                  bill?.stale ? "opacity-60" : ""
                }`}
              >
                {run.summary}
              </span>
            )}

            {(run?.blocking_failures?.length ?? 0) > 0 && (
              <div className="flex w-full flex-col gap-2 rounded-md border border-solid border-error-200 bg-error-50 p-3">
                <span className="text-body-bold font-body-bold text-default-font">
                  Blocking checks that failed
                </span>
                <div className="flex flex-wrap gap-1">
                  {run!.blocking_failures.map((key) => (
                    <Badge key={key} variant="error">
                      {key}
                    </Badge>
                  ))}
                </div>
                <span className="text-caption font-caption text-subtext-color">
                  These come from the rule validator, not the agent. A bill is
                  never Ready while one of them fails.
                </span>
              </div>
            )}

            {blocking.length > 0 && (
              <div className="flex w-full flex-col gap-3">
                <span className="text-body-bold font-body-bold text-default-font">
                  Does not match the document ({blocking.length})
                </span>
                {blocking.map((f) => (
                  <FindingCard
                    key={f.id}
                    finding={f}
                    canEdit={canEdit}
                    onDisposition={disposition}
                  />
                ))}
              </div>
            )}

            {advisory.length > 0 && (
              <div className="flex w-full flex-col gap-3">
                <span className="text-body-bold font-body-bold text-default-font">
                  Worth a look ({advisory.length})
                </span>
                <span className="text-caption font-caption text-subtext-color">
                  The document is captured faithfully; these are booking choices
                  that look unlike this vendor's history. They do not block
                  posting.
                </span>
                {advisory.map((f) => (
                  <FindingCard
                    key={f.id}
                    finding={f}
                    canEdit={canEdit}
                    onDisposition={disposition}
                  />
                ))}
              </div>
            )}

            {run?.status === "done" && blocking.length === 0 && advisory.length === 0 && (
              <Alert
                variant="success"
                title="Nothing to flag"
                description="The bill matches its document and its booking follows this vendor's history."
              />
            )}

            {(run?.cannot_verify?.length ?? 0) > 0 && (
              <div className="flex w-full flex-col gap-1">
                <span className="text-body-bold font-body-bold text-default-font">
                  Could not be checked
                </span>
                {run!.cannot_verify.map((line, i) => (
                  <span
                    key={i}
                    className="text-caption font-caption text-subtext-color"
                  >
                    · {line}
                  </span>
                ))}
              </div>
            )}

            {run && (
              <span className="text-caption font-caption text-subtext-color">
                {run.model} · prompt {run.prompt_version} · {run.trigger_type} ·{" "}
                {run.finished_at || "unfinished"}
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-neutral-border px-6 py-4">
          {variant === "dashboard" ? (
            <>
              <Button variant="neutral-secondary" onClick={onClose}>
                Close
              </Button>
              {/* Same tab, same address, same behaviour as the queue drawer's
                  button. It was a new-tab anchor in change 7; the user reversed
                  that — going to the bill is the task, not a side trip, and a
                  new tab leaves them to close it. */}
              <Button
                icon={<FeatherExternalLink />}
                onClick={() =>
                  navigate(`/bills/review?documentId=${bill?.bill_id ?? ""}`)
                }
              >
                Open in Review
              </Button>
            </>
          ) : (
            <>
          {canEdit && (
            <Button
              variant="neutral-secondary"
              icon={<FeatherRefreshCw />}
              loading={rechecking}
              onClick={handleRecheck}
            >
              Re-check
            </Button>
          )}
          {/* An anchor for the same reason as the row icon: a link cannot be
              refused by a popup blocker, and it gives cmd-click for free.
              Styled to match a neutral-secondary Button. */}
          <a
            href={`/bills/review?documentId=${bill?.bill_id ?? ""}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex h-8 cursor-pointer items-center justify-center gap-2 rounded-md border border-solid border-neutral-border bg-default-background px-3 py-0 hover:bg-neutral-50 active:bg-default-background"
          >
            <FeatherExternalLink className="text-body font-body text-neutral-700" />
            <span className="whitespace-nowrap text-body-bold font-body-bold text-neutral-700">
              Open in new tab
            </span>
          </a>
          <Button
            icon={<FeatherExternalLink />}
            onClick={() =>
              navigate(`/bills/review?documentId=${bill?.bill_id ?? ""}`)
            }
          >
            Open in review
          </Button>
            </>
          )}
        </div>
      </Drawer.Content>
    </Drawer>
  );
};

export default AgentReviewDrawer;
